<template>
    <div class="ui-page">
        <ui-page-header title="Ativos" subtitle="Bens do processo com laudo, valor de avaliação, localização e fotos.">
            <v-btn outlined :disabled="!rows.length" @click="exportRows"><v-icon left small>mdi-microsoft-excel</v-icon>Exportar (.xlsx)</v-btn>
            <v-btn v-if="processId && $allowed('create')" color="primary" depressed @click="openAsset(null)"><v-icon left small>mdi-plus</v-icon>Novo ativo</v-btn>
        </ui-page-header>

        <div v-if="summary" class="cp-kpis">
            <div class="cp-kpi"><div class="cp-kpi__head">Ativos<v-icon small>mdi-domain</v-icon></div><div class="cp-kpi__value">{{ summary.total }}</div><div class="cp-kpi__hint">{{ summary.cities }} {{ summary.cities === 1 ? "município" : "municípios" }}</div></div>
            <div class="cp-kpi"><div class="cp-kpi__head">Valor de avaliação<v-icon small>mdi-bank-outline</v-icon></div><div class="cp-kpi__value">{{ formatMoneyShort(summary.appraisal_total) }}</div><div class="cp-kpi__hint">soma dos laudos filtrados</div></div>
        </div>

        <ui-table-card clearable :can-clear="hasFilters" :more-count="moreCount" :more-open="moreCount > 0" @clear="clearFilters">
            <template #toolbar>
                <competitive-process-picker v-model="processId" />
                <v-text-field v-model="search" placeholder="Código, descrição, matrícula ou cidade" dense outlined hide-details prepend-inner-icon="mdi-magnify" class="ui-toolbar__search"></v-text-field>
                <v-select v-model="filters.status" :items="options.asset_statuses" placeholder="Todas as situações" clearable dense outlined hide-details class="ui-toolbar__field" :menu-props="{ offsetY: true }"></v-select>
            </template>
            <template #more>
                <v-select v-model="filters.asset_type" :items="options.asset_types" placeholder="Todos os tipos" clearable dense outlined hide-details class="ui-toolbar__field" :menu-props="{ offsetY: true }"></v-select>
                <v-autocomplete v-model="filters.state" :items="options.states" placeholder="UF" clearable dense outlined hide-details class="ui-toolbar__field" style="max-width: 120px;" :menu-props="{ offsetY: true }"></v-autocomplete>
                <v-select v-model="filters.without" :items="withoutItems" placeholder="Pendência de cadastro" clearable dense outlined hide-details class="ui-toolbar__field" :menu-props="{ offsetY: true }"></v-select>
            </template>
            <v-data-table :headers="headers" :items="rows" :loading="loading" disable-sort hide-default-footer :page.sync="page" :items-per-page="itemsPerPage"
                :no-data-text="processId ? 'Nenhum ativo com esses filtros' : 'Selecione um processo'" loading-text="Carregando..." class="ui-clickable-rows" @click:row="openAsset">
                <template v-slot:[`item.code`]="{ item }"><span class="ui-mono">{{ item.code }}</span></template>
                <template v-slot:[`item.title`]="{ item }">
                    <div class="ui-cell-main">{{ item.title }}</div>
                    <div class="ui-cell-sub">{{ item.asset_type_label }}<span v-if="item.registration"> · Matrícula {{ item.registration }}</span></div>
                </template>
                <template v-slot:[`item.city`]="{ item }">{{ item.city ? `${item.city}${item.state ? `/${item.state}` : ""}` : "—" }}</template>
                <template v-slot:[`item.appraisal_value`]="{ item }">
                    <span class="cp-num">{{ formatMoney(item.appraisal_value) }}</span>
                    <div class="ui-cell-sub">
                        <span v-if="item.has_appraisal_file">laudo<span v-if="item.appraisal_date"> de {{ formatDate(item.appraisal_date) }}</span></span>
                        <span v-else class="cp-warn">sem laudo</span>
                    </div>
                </template>
                <template v-slot:[`item.groups`]="{ item }">
                    <span v-if="!item.groups.length" class="ui-muted">—</span>
                    <span v-else class="ui-small">{{ item.groups.map((group) => group.code).join(", ") }}</span>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge set="cp-asset" :status="item.status" />
                    <div v-if="item.block_reason" class="ui-cell-sub" :title="item.block_reason">{{ item.block_reason.length > 40 ? `${item.block_reason.slice(0, 40)}…` : item.block_reason }}</div>
                </template>
                <template v-slot:[`item.actions`]><v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon></template>
            </v-data-table>
            <template #footer>
                <ui-pagination :total="rows.length" :page.sync="page" :items-per-page.sync="itemsPerPage" />
            </template>
        </ui-table-card>

    </div>
</template>

<script>
import axios from "axios";
import { downloadXlsx } from "@/common/util/export-xlsx.util";
import CompetitiveProcessPicker from "@/components/competitive/CompetitiveProcessPicker.vue";
import processScope from "@/components/competitive/process-scope";
import { API, formatDate, formatMoney, formatMoneyShort, loadOptions, notifyError } from "@/components/competitive/competitive.util";

export default {
    name: "CompetitiveAssetList",
    components: { CompetitiveProcessPicker },
    mixins: [processScope],
    data: () => ({
        options: { asset_statuses: [], asset_types: [], states: [] },
        rows: [],
        summary: null,
        loading: false,
        search: "",
        searchTimer: null,
        filters: { status: null, asset_type: null, state: null, without: null },
        page: 1,
        itemsPerPage: 25,
        withoutItems: [
            { value: "appraisal", text: "Sem valor de avaliação" },
            { value: "file", text: "Sem laudo" },
            { value: "coordinates", text: "Sem localização" },
            { value: "media", text: "Sem fotos" },
        ],
        headers: [
            { text: "Código", value: "code", width: 90 },
            { text: "Ativo", value: "title" },
            { text: "Município", value: "city", width: 180 },
            { text: "Avaliação", value: "appraisal_value", width: 180 },
            { text: "Agrupamentos", value: "groups", width: 140 },
            { text: "Situação", value: "status", width: 150 },
            { text: "", value: "actions", align: "end", width: 40 },
        ],
    }),
    computed: {
        moreCount() {
            return ["asset_type", "state", "without"].filter((key) => this.filters[key]).length;
        },
        hasFilters() {
            return !!this.search || Object.values(this.filters).some(Boolean);
        },
    },
    watch: {
        filters: {
            deep: true,
            handler() {
                this.load();
            },
        },
        search() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.load(), 300);
        },
    },
    async created() {
        try {
            this.options = await loadOptions();
        } catch (error) {
            notifyError(this, error);
        }
        // Filtro vindo do painel (ex.: ?sem=file).
        if (this.$route.query.sem) this.filters.without = this.$route.query.sem;
    },
    methods: {
        formatDate,
        formatMoney,
        formatMoneyShort,
        onProcess() {
            this.page = 1;
            this.load();
        },
        clearFilters() {
            this.search = "";
            this.filters = { status: null, asset_type: null, state: null, without: null };
        },
        async load() {
            if (!this.processId) return;
            this.loading = true;
            try {
                const params = { search: this.search || undefined };
                Object.entries(this.filters).forEach(([key, value]) => { if (value) params[key] = value; });
                const result = await axios.get(`${API}/processes/${this.processId}/assets`, { params });
                this.rows = result.data.content.data;
                this.summary = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
        openAsset(item) {
            if (item) this.$router.push({ path: `/processo-competitivo/ativos/${item.id}` });
            else this.$router.push({ path: "/processo-competitivo/ativos/novo", query: { processo: String(this.processId) } });
        },
        exportRows() {
            downloadXlsx(this.rows.map((item) => ({
                Código: item.code,
                Ativo: item.title,
                Tipo: item.asset_type_label,
                Matrícula: item.registration || "",
                Cartório: item.registry_office || "",
                Endereço: item.address || "",
                Município: item.city || "",
                UF: item.state || "",
                Latitude: item.latitude === null ? "" : item.latitude,
                Longitude: item.longitude === null ? "" : item.longitude,
                Área: item.area === null ? "" : item.area,
                Unidade: item.area_unit || "",
                "Valor de avaliação": item.appraisal_value === null ? "" : item.appraisal_value,
                "Data do laudo": item.appraisal_date ? formatDate(item.appraisal_date) : "",
                Laudo: item.has_appraisal_file ? item.appraisal_file_name || "Sim" : "",
                Agrupamentos: item.groups.map((group) => group.code).join(", "),
                Situação: item.status_label,
                Motivo: item.block_reason || "",
            })), "Ativos_processo_competitivo");
        },
    },
};
</script>

<style lang="scss">
@import "../../components/competitive/competitive";
</style>
